/**
 * inventoryValuation.ts — Pure computation of stock valuation as of any date:
 * what the inventory was worth (at cost and at retail) at a chosen point in
 * time. Accountants need the closing-stock value at a month/year end, which the
 * live "current stock" figure can't give once stock has moved since.
 *
 * It works backwards from live stock: the quantity as of `asOf` is
 *   currentStock − Σ(signed delta of every movement strictly after asOf),
 * where the delta is the canonical `productStockDelta` (so a rewound quantity
 * reconciles with how current stock was derived in the first place). The screen
 * supplies `deltaAfter`; this file only nets and values it.
 *
 * Pure — see `inventoryValuationDataSource.ts` for the WatermelonDB bridge.
 */

export function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

// ─── Inputs ──────────────────────────────────────────────────────────────────

export interface ValuationProductInput {
  id: string;
  name: string;
  category: string;
  currentStock: number;
  purchasePrice: number;
  sellingPrice: number;
  /** Σ signed stock delta of movements strictly after `asOf`. */
  deltaAfter: number;
}

export interface InventoryValuationInput {
  products: ValuationProductInput[];
  asOf: Date;
}

// ─── Output ──────────────────────────────────────────────────────────────────

export interface ValuationRow {
  productId: string;
  name: string;
  category: string;
  quantity: number;
  costValue: number;
  retailValue: number;
}

export interface CategoryValuation {
  category: string;
  quantity: number;
  costValue: number;
  retailValue: number;
}

export interface InventoryValuation {
  /** Products holding stock as of the date, most valuable first. */
  rows: ValuationRow[];
  byCategory: CategoryValuation[];
  totalCost: number;
  totalRetail: number;
  totalUnits: number;
  skuCount: number;
  asOf: Date;
}

export function buildInventoryValuation(input: InventoryValuationInput): InventoryValuation {
  const { products, asOf } = input;

  let totalCost = 0;
  let totalRetail = 0;
  let totalUnits = 0;
  const catMap = new Map<string, { quantity: number; cost: number; retail: number }>();

  const rows: ValuationRow[] = [];
  for (const p of products) {
    const quantity = round2(p.currentStock - p.deltaAfter);
    // Only products that actually held stock at the date. Negative rewinds
    // (data gaps) are treated as zero — you can't value negative stock.
    if (quantity <= 0) continue;
    const costValue = round2(quantity * p.purchasePrice);
    const retailValue = round2(quantity * p.sellingPrice);
    totalCost += costValue;
    totalRetail += retailValue;
    totalUnits += quantity;

    const c = catMap.get(p.category) ?? { quantity: 0, cost: 0, retail: 0 };
    c.quantity += quantity;
    c.cost += costValue;
    c.retail += retailValue;
    catMap.set(p.category, c);

    rows.push({ productId: p.id, name: p.name, category: p.category, quantity, costValue, retailValue });
  }

  rows.sort((a, b) => b.costValue - a.costValue);

  const byCategory: CategoryValuation[] = [...catMap.entries()]
    .map(([category, v]) => ({ category, quantity: round2(v.quantity), costValue: round2(v.cost), retailValue: round2(v.retail) }))
    .sort((a, b) => b.costValue - a.costValue);

  return {
    rows,
    byCategory,
    totalCost: round2(totalCost),
    totalRetail: round2(totalRetail),
    totalUnits: round2(totalUnits),
    skuCount: rows.length,
    asOf,
  };
}
